import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
    Alert,
    Divider,
    Link,
} from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BaseAPIs from '../../apis/base.apis';
import logo from '../../assets/logo.png';

const BRAND_PURPLE = '#603084';
const BRAND_PURPLE_LIGHT = 'rgba(96, 48, 132, 0.07)';
const SUPPORT_NUMBER = '+91 9920059762';
const SUPPORT_NUMBER_RAW = '+919920059762';

export default function VerifyCertificate() {
    const [certificateNumber, setCertificateNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [certificateDetails, setCertificateDetails] = useState<any>(null);

    const handleInputChange = (value: string) => {
        setCertificateNumber(value);
        if (error) setError('');
        if (certificateDetails) setCertificateDetails(null);
    };

    const isExpired = (validityDateStr: string) => {
        if (!validityDateStr) return false;
        // Parses dates in DD-MM-YYYY format or standard YYYY-MM-DD / ISO format
        let validTill: Date;
        if (validityDateStr.includes('-')) {
            const parts = validityDateStr.split('-');
            if (parts[0].length === 2 && parts[2].length === 4) {
                // DD-MM-YYYY
                validTill = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]), 23, 59, 59);
            } else {
                validTill = new Date(validityDateStr);
                validTill.setHours(23, 59, 59, 999);
            }
        } else {
            validTill = new Date(validityDateStr);
            validTill.setHours(23, 59, 59, 999);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return validTill < today;
    };

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmed = certificateNumber.trim();
        if (!trimmed) {
            setError('Please enter an Operator Certificate Number.');
            return;
        }

        setLoading(true);
        setError('');
        setCertificateDetails(null);

        const response = await new BaseAPIs().get(
            `/_api/v1/public/operator-certificate/verify/${encodeURIComponent(trimmed)}?_t=${Date.now()}`
        );

        setLoading(false);

        if (response && response.success) {
            const validTillDate = response.date_of_validity || response.valid_till || response.due_date;
            setCertificateDetails({
                certificate_number: response.certificate_number,
                name: response.name,
                category: response.category,
                mobile_number: response.mobile_number,
                date_of_certification: response.date_of_certification || response.issue_date,
                date_of_validity: validTillDate,
                is_expired: isExpired(validTillDate),
            });
        } else {
            setError(
                response?.message || 'The entered Operator Certificate Number could not be found.'
            );
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f5f0fa 0%, #f0f4ff 100%)',
                px: 2,
                py: 4,
            }}
        >
            {/* Verification Card */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 480,
                    bgcolor: '#fff',
                    borderRadius: '20px',
                    boxShadow: '0px 8px 40px rgba(96, 48, 132, 0.10), 0px 2px 8px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {/* Card Header Band */}
                <Box
                    sx={{
                        bgcolor: '#fff',
                        px: 4,
                        pt: 4,
                        pb: 2,
                        textAlign: 'center',
                        borderBottom: '1px solid #f0f0f0'
                    }}
                >
                    {/* Logo */}
                    <Box display="flex" justifyContent="center" mb={2.5}>
                        <img
                            alt="Uniquo"
                            src={logo}
                            style={{ height: 46, objectFit: 'contain' }}
                        />
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{ color: BRAND_PURPLE, fontWeight: 800, lineHeight: 1.3, mb: 1 }}
                    >
                        Verify Competency Certificate
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#65748B', lineHeight: 1.6 }}
                    >
                        Enter Certificate Number to verify the authenticity
                    </Typography>
                </Box>

                {/* Card Body */}
                <Box sx={{ px: 4, py: 4 }}>
                    <form onSubmit={handleVerify}>
                        <TextField
                            fullWidth
                            label="Certificate Number"
                            variant="outlined"
                            value={certificateNumber}
                            onChange={(e) => handleInputChange(e.target.value)}
                            placeholder="e.g. MH-24A0001-ENG01"
                            disabled={loading}
                            autoComplete="off"
                            inputProps={{ style: { textTransform: 'uppercase', letterSpacing: '0.5px' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <BadgeOutlinedIcon sx={{ color: BRAND_PURPLE, fontSize: 22 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    '&:hover fieldset': { borderColor: BRAND_PURPLE },
                                    '&.Mui-focused fieldset': { borderColor: BRAND_PURPLE },
                                },
                                '& label.Mui-focused': { color: BRAND_PURPLE },
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            size="large"
                            disabled={loading || !certificateNumber.trim()}
                            sx={{
                                bgcolor: BRAND_PURPLE,
                                color: '#fff',
                                borderRadius: '12px',
                                py: 1.6,
                                fontSize: '1rem',
                                fontWeight: 600,
                                letterSpacing: '0.3px',
                                transition: 'background 0.2s, transform 0.15s, box-shadow 0.2s',
                                '&:hover:not(:disabled)': {
                                    bgcolor: '#4e2570',
                                    boxShadow: '0 4px 18px rgba(96,48,132,0.35)',
                                    transform: 'translateY(-1px)',
                                },
                                '&:active:not(:disabled)': {
                                    transform: 'translateY(0)',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: 'rgba(96,48,132,0.3)',
                                    color: '#fff',
                                },
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={22} sx={{ color: '#fff' }} />
                            ) : (
                                'Verify Certificate'
                            )}
                        </Button>
                    </form>

                    {/* Result State */}
                    {certificateDetails && (
                        <Box
                            sx={{
                                mt: 3,
                                p: 2.5,
                                bgcolor: certificateDetails.is_expired ? '#fff5f5' : '#f0fdf4',
                                border: `1.5px solid ${certificateDetails.is_expired ? '#fecaca' : '#bbf7d0'}`,
                                borderRadius: '12px',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                                {certificateDetails.is_expired ? (
                                    <ErrorOutlineIcon sx={{ color: '#dc2626', fontSize: 22 }} />
                                ) : (
                                    <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 22 }} />
                                )}
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: certificateDetails.is_expired ? '#b91c1c' : '#15803d',
                                        fontWeight: 700
                                    }}
                                >
                                    {certificateDetails.is_expired ? 'Certificate Expired' : 'Certificate Verified Successfully'}
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: certificateDetails.is_expired ? '#7f1d1d' : '#166534',
                                    mb: 2
                                }}
                            >
                                {certificateDetails.is_expired
                                    ? ''
                                    : 'The original details of the mentioned Certificate issued by us are as follows:'}
                            </Typography>
                            
                            <Divider sx={{ my: 2, borderColor: certificateDetails.is_expired ? '#fecaca' : '#bbf7d0' }} />
                            
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: certificateDetails.is_expired ? '#b91c1c' : '#15803d',
                                    fontWeight: 700,
                                    mb: 1.5
                                }}
                            >
                                Certificate Details:
                            </Typography>
                            
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        variant="body2"
                                        sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534', fontWeight: 600 }}
                                    >
                                        Certificate Number
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534' }}>
                                        {certificateDetails.certificate_number || '-'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        variant="body2"
                                        sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534', fontWeight: 600 }}
                                    >
                                        Name
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534' }}>
                                        {certificateDetails.name || '-'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        variant="body2"
                                        sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534', fontWeight: 600 }}
                                    >
                                        Category
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534' }}>
                                        {certificateDetails.category || '-'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        variant="body2"
                                        sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534', fontWeight: 600 }}
                                    >
                                        Mobile Number
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534' }}>
                                        {certificateDetails.mobile_number 
                                            ? (certificateDetails.mobile_number.length > 5 
                                                ? `*****${certificateDetails.mobile_number.slice(-5)}` 
                                                : certificateDetails.mobile_number)
                                            : '-'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        variant="body2"
                                        sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534', fontWeight: 600 }}
                                    >
                                        Date of Certification
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534' }}>
                                        {certificateDetails.date_of_certification || '-'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        variant="body2"
                                        sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534', fontWeight: 600 }}
                                    >
                                        Certificate valid till
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: certificateDetails.is_expired ? '#7f1d1d' : '#166534' }}>
                                        {certificateDetails.date_of_validity || '-'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Error State */}
                    {error && (
                        <Box
                            sx={{
                                mt: 3,
                                p: 2.5,
                                bgcolor: '#fff5f5',
                                border: '1.5px solid #fecaca',
                                borderRadius: '12px',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <ErrorOutlineIcon sx={{ color: '#dc2626', fontSize: 22 }} />
                                <Typography variant="subtitle2" sx={{ color: '#b91c1c', fontWeight: 700 }}>
                                    Certificate Not Found
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#7f1d1d', lineHeight: 1.6, mb: 1 }}>
                                {error}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
                                Please verify that the certificate number is entered correctly. If you still cannot find the certificate, kindly contact our support team for verification via Call or WhatsApp.
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Footer */}
                <Divider />
                <Box
                    sx={{
                        px: 4,
                        py: 3,
                        bgcolor: '#fafafa',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="caption" sx={{ color: '#65748B', display: 'block', mb: 1.5 }}>
                        Need help verifying a certificate?
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                        <Link
                            href={`tel:${SUPPORT_NUMBER_RAW}`}
                            underline="none"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: BRAND_PURPLE,
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                '&:hover': { color: '#4e2570' },
                            }}
                        >
                            <PhoneIcon sx={{ fontSize: 16 }} />
                            {SUPPORT_NUMBER}
                        </Link>
                        <Link
                            href={`https://wa.me/${SUPPORT_NUMBER_RAW.replace('+', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#16a34a',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                '&:hover': { color: '#15803d' },
                            }}
                        >
                            <WhatsAppIcon sx={{ fontSize: 16 }} />
                            WhatsApp
                        </Link>
                    </Box>
                </Box>
            </Box>

            {/* Powered by line */}
            <Typography variant="caption" sx={{ mt: 3, color: '#9ca3af' }}>
                © {new Date().getFullYear()} Uniquo. All rights reserved.
            </Typography>
        </Box>
    );
}
